import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Chip,
  TablePagination
} from '@mui/material';
import { Search as SearchIcon, GetApp as DownloadIcon } from '@mui/icons-material';
import { api } from '../services/api';
import { authService } from '../services/auth';
import { getPortalAccess, resolveProviderId } from '../utils/portalAccess';
import { Provider, Client, Visit, OhioCounty, Service, ServiceUnitConversion, Transportation, Location, ClientISP, ClientISPOutcome, ClientISPExperience, ClientISPSupport, UiRecord } from '../types';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { generateServicesReportPDF, generateTransportationReportPDF } from '../utils/pdfGenerator';
import { dateToYmdInEastern, ymdToLocalDate } from '../utils/easternTime';
import AppDateField from '../components/AppDateField';

interface ComplianceReportingProps {
  selectedProvider?: number | null;
}

interface UnusualIncidentReportRow {
  date: string;
  sortDate: number;
  clientId: number;
  clientName: string;
  hasUnusualIncident: boolean;
  description: string;
  injury: string;
  actionsTaken: string;
  causesContributingFactors: string;
  isMajorUnusualIncident: boolean;
}

type ComplianceReportType =
  | 'services'
  | 'transportation'
  | 'unusual-incidents'
  | 'hpc-service-delivery'
  | 'hpc-outcome-documentation';

const isMonthlyOfficialReport = (reportType: ComplianceReportType): boolean =>
  reportType === 'hpc-service-delivery';

const isIspScopedOfficialReport = (reportType: ComplianceReportType): boolean =>
  reportType === 'hpc-outcome-documentation';

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const monthBounds = (year: number, month: number): { startDate: string; endDate: string } => {
  const lastDay = new Date(year, month, 0).getDate();
  return {
    startDate: `${year}-${String(month).padStart(2, '0')}-01`,
    endDate: `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
  };
};

// Helper function to format date as MM-DD-YYYY
const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '';
  try {
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return '';
    }
    return `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}-${year}`;
  } catch (error) {
    return '';
  }
};

// Helper function to format time as HH:MM AM/PM
const formatTime = (dateString: string | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '';
    }
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const minutesStr = String(minutes).padStart(2, '0');
    return `${hours}:${minutesStr} ${ampm}`;
  } catch (error) {
    return '';
  }
};

const ComplianceReporting: React.FC<ComplianceReportingProps> = ({ selectedProvider: adminSelectedProvider }) => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [transportations, setTransportations] = useState<Transportation[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [outcomes, setOutcomes] = useState<ClientISPOutcome[]>([]);
  const [experiences, setExperiences] = useState<ClientISPExperience[]>([]);
  const [supports, setSupports] = useState<ClientISPSupport[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [ohioCounties, setOhioCounties] = useState<OhioCounty[]>([]);
  const [serviceUnitConversions, setServiceUnitConversions] = useState<ServiceUnitConversion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Selection states
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isps, setIsps] = useState<ClientISP[]>([]);
  const [selectedISP, setSelectedISP] = useState<ClientISP | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [reportType, setReportType] = useState<ComplianceReportType>('services');
  const [serviceMonth, setServiceMonth] = useState<number>(() => new Date().getMonth() + 1);
  const [serviceYear, setServiceYear] = useState<number>(() => new Date().getFullYear());
  const [incidentRows, setIncidentRows] = useState<UnusualIncidentReportRow[]>([]);
  const [isDownloadingOfficialPdf, setIsDownloadingOfficialPdf] = useState(false);
  const [hpcPreviewReady, setHpcPreviewReady] = useState(false);
  const [outcomePreviewReady, setOutcomePreviewReady] = useState(false);
  
  // Table pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const serviceYearOptions = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 8 }, (_, i) => currentYear - 5 + i);
  }, []);

  useEffect(() => {
    fetchProviders();
    fetchOhioCounties();
    fetchServiceUnitConversions();
    setAdminStatus();
  }, [adminSelectedProvider]);

  const setAdminStatus = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setIsAdmin(currentUser.attributes['custom:admin_flag'] === 'true');
    } catch (err) {
      console.error('Error getting admin status:', err);
      setIsAdmin(false);
    }
  };

  const fetchProviders = async () => {
    try {
      setIsLoading(true);
      const access = await getPortalAccess();
      const currentUser = await authService.getCurrentUser();
      const providerId = resolveProviderId(
        access,
        adminSelectedProvider,
        currentUser.attributes['custom:provider_id'],
      );

      const response = await api.providers.list(0, 100);
      setProviders(response);

      if (providerId) {
        const provider = response.find((p: Provider) => Number(p.provider_id) === providerId)
          ?? await api.providers.get(providerId);
        setSelectedProvider(provider);
        await fetchClientsByProvider(provider.provider_id);
      } else if (access.requiresProviderSelection) {
        setSelectedProvider(null);
        setClients([]);
      } else if (access.isAdmin) {
        setSelectedProvider(null);
      }
    } catch (err) {
      console.error('Error fetching providers:', err);
      setError('Failed to load providers. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClientsByProvider = async (providerId: number | string) => {
    try {
      setIsLoading(true);
      const response = await api.clients.listByProvider(providerId, 100);
      setClients(response);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Failed to load clients. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOhioCounties = async () => {
    try {
      const response = await api.ohioCounty.getAll();
      setOhioCounties(response);
    } catch (err) {
      console.error('Error fetching Ohio counties:', err);
    }
  };

  const fetchServiceUnitConversions = async () => {
    try {
      const response = await api.serviceUnitConversion.getAll();
      setServiceUnitConversions(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Error fetching service unit conversions:', err);
    }
  };

  const fetchVisits = async (overrideStartDate?: string, overrideEndDate?: string) => {
    const effectiveStartDate = overrideStartDate || startDate;
    const effectiveEndDate = overrideEndDate || endDate;

    if (!selectedProvider || !selectedClient || !effectiveStartDate || !effectiveEndDate) {
      setError('Please select a provider, client, and date range.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch visits with filters
      const response = await api.visits.listByProvider(
        selectedProvider.provider_id,
        1000, // Large limit to get all visits
        0,
        {
          startDate: effectiveStartDate,
          endDate: effectiveEndDate,
          clientId: Number(selectedClient.client_id)
        }
      );

      // Handle both array response and paginated response
      let visitsData: Visit[] = [];
      if (Array.isArray(response)) {
        visitsData = response;
      } else if (response && typeof response === 'object' && 'items' in response) {
        visitsData = response.items || [];
      }
      
      // Filter by isp_id if ISP is selected
      if (selectedISP) {
        visitsData = visitsData.filter((visit) => visit.isp_id === selectedISP.isp_id);
      }
      
      setVisits(visitsData);

      // Fetch outcomes, experiences, and supports from ISP endpoints
      // Collect unique ISP IDs from the visits to ensure we fetch all relevant data
      const uniqueISPIds = new Set<number>();
      visitsData.forEach(visit => {
        if (visit.isp_id) {
          uniqueISPIds.add(visit.isp_id);
        }
      });

      let allOutcomes: ClientISPOutcome[] = [];
      let allExperiences: ClientISPExperience[] = [];
      let allSupports: ClientISPSupport[] = [];

      if (selectedISP) {
        // Fetch from selected ISP only
        try {
          const [outcomesRes, experiencesRes, supportsRes] = await Promise.all([
            api.clientISP.outcomes.list(selectedISP.isp_id),
            api.clientISP.experiences.list(selectedISP.isp_id),
            api.clientISP.supports.list(selectedISP.isp_id)
          ]);
          // Validate that responses contain the correct data types
          allOutcomes = Array.isArray(outcomesRes) 
            ? outcomesRes.filter((item: any) => item && 'outcome_id' in item && !('support_id' in item))
            : [];
          allExperiences = Array.isArray(experiencesRes) 
            ? experiencesRes.filter((item: any) => item && 'experience_id' in item)
            : [];
          allSupports = Array.isArray(supportsRes) 
            ? supportsRes.filter((item: any) => item && 'support_id' in item && !('outcome_id' in item))
            : [];
        } catch (err) {
          console.error('Error fetching ISP data:', err);
        }
      } else {
        // Fetch from all ISPs that have visits in the date range, plus any client ISPs
        try {
          // Get all ISPs associated with visits
          const ispIdsArray = Array.from(uniqueISPIds);
          
          // Also get all ISPs for the client (active and inactive) to ensure we have complete data
          const clientISPs = await api.clientISP.getByClient(selectedClient.client_id);
          const clientISPsArray = Array.isArray(clientISPs) ? clientISPs : [];
          
          // Combine ISP IDs from visits and client ISPs
          const allISPIds = new Set(ispIdsArray);
          clientISPsArray.forEach(isp => {
            if (isp.isp_id) {
              allISPIds.add(isp.isp_id);
            }
          });
          
          // Fetch outcomes, experiences, and supports for all relevant ISPs
          const allISPIdsArray = Array.from(allISPIds);
          for (const ispId of allISPIdsArray) {
            try {
              const [outcomesRes, experiencesRes, supportsRes] = await Promise.all([
                api.clientISP.outcomes.list(ispId),
                api.clientISP.experiences.list(ispId),
                api.clientISP.supports.list(ispId)
              ]);
              // Validate that responses contain the correct data types
              const validOutcomes = Array.isArray(outcomesRes) 
                ? outcomesRes.filter((item: any) => item && 'outcome_id' in item && !('support_id' in item))
                : [];
              const validExperiences = Array.isArray(experiencesRes) 
                ? experiencesRes.filter((item: any) => item && 'experience_id' in item)
                : [];
              const validSupports = Array.isArray(supportsRes) 
                ? supportsRes.filter((item: any) => item && 'support_id' in item && !('outcome_id' in item))
                : [];
              allOutcomes.push(...validOutcomes);
              allExperiences.push(...validExperiences);
              allSupports.push(...validSupports);
            } catch (err) {
              console.error(`Error fetching data for ISP ${ispId}:`, err);
            }
          }
        } catch (err) {
          console.error('Error fetching ISPs:', err);
        }
      }

      setOutcomes(allOutcomes);
      setExperiences(allExperiences);
      setSupports(allSupports);

      // Fetch services (for place of service and units calculation)
      try {
        const servicesData = await api.services.list(0, 1000);
        setServices(Array.isArray(servicesData) ? servicesData : []);
      } catch (err) {
        console.error('Error fetching services:', err);
      }

      if (reportType === 'hpc-service-delivery') {
        setHpcPreviewReady(true);
      }
      if (reportType === 'hpc-outcome-documentation') {
        setOutcomePreviewReady(true);
      }

    } catch (err) {
      console.error('Error fetching visits:', err);
      setError('Failed to load visits. Please try again later.');
      setHpcPreviewReady(false);
      setOutcomePreviewReady(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProviderChange = (provider: Provider | null) => {
    setSelectedProvider(provider);
    setSelectedClient(null);
    setClients([]);
    setVisits([]);
    setIncidentRows([]);
    
    if (provider) {
      fetchClientsByProvider(provider.provider_id);
    }
  };

  const handleClientChange = (client: Client | null) => {
    setSelectedClient(client);
    setSelectedISP(null);
    setIsps([]);
    setVisits([]);
    setTransportations([]);
    setIncidentRows([]);
    setHpcPreviewReady(false);
    setOutcomePreviewReady(false);
    if (client && selectedProvider) {
      fetchISPsForClient(client.client_id);
    }
  };

  const fetchISPsForClient = async (clientId: number | string) => {
    try {
      setIsLoading(true);
      const response = await api.clientISP.getByClient(clientId);
      setIsps(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Error fetching ISPs:', err);
      setIsps([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Clamp a date to be within ISP span when ISP is selected
  const clampDateToISPSpan = (
    date: string,
    isp: ClientISP | null,
    type: 'start' | 'end',
    otherDate: string
  ): string => {
    if (!isp || !date) return date;
    const dateVal = new Date(date);
    if (isNaN(dateVal.getTime())) return date;

    const spanStart = isp.span_start_date ? new Date(isp.span_start_date) : null;
    const spanEnd = isp.span_end_date ? new Date(isp.span_end_date) : null;

    let result = dateVal;
    if (spanStart && result < spanStart) result = spanStart;
    if (spanEnd && result > spanEnd) result = spanEnd;

    // Ensure start <= end and end >= start
    if (otherDate) {
      const other = new Date(otherDate);
      if (type === 'start' && result > other) result = other;
      if (type === 'end' && result < other) result = other;
    }

    return dateToYmdInEastern(result);
  };

  const handleISPChange = (isp: ClientISP | null) => {
    setSelectedISP(isp);
    if (isp) {
      // Clamp existing dates to ISP span (do not overwrite with ISP span - keep user selection within bounds)
      setStartDate((prev) => {
        if (!prev) return prev;
        return clampDateToISPSpan(prev, isp, 'start', endDate);
      });
      setEndDate((prev) => {
        if (!prev) return prev;
        return clampDateToISPSpan(prev, isp, 'end', startDate);
      });
    }
  };

  const handleStartDateChange = (value: string) => {
    const clamped = selectedISP
      ? clampDateToISPSpan(value, selectedISP, 'start', endDate)
      : value;
    setStartDate(clamped);
  };

  const handleEndDateChange = (value: string) => {
    const clamped = selectedISP
      ? clampDateToISPSpan(value, selectedISP, 'end', startDate)
      : value;
    setEndDate(clamped);
  };

  const getClientName = (clientId: number): string => {
    const client = clients.find(c => Number(c.client_id) === clientId);
    return client ? `${client.first_name} ${client.last_name}` : '';
  };

  const parseVisitsResponse = (response: Visit[] | { items?: Visit[] }): Visit[] => {
    if (Array.isArray(response)) {
      return response;
    }
    if (response && typeof response === 'object' && 'items' in response) {
      return response.items || [];
    }
    return [];
  };

  const fetchUnusualIncidents = async () => {
    if (!selectedProvider || !startDate || !endDate) {
      setError('Please select a provider and date range.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const uiParams: { provider_id: number; client_id?: number } = {
        provider_id: Number(selectedProvider.provider_id),
      };
      if (selectedClient) {
        uiParams.client_id = Number(selectedClient.client_id);
      }

      const uiRecords: UiRecord[] = await api.ui.list(uiParams);

      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);
      endDateObj.setHours(23, 59, 59, 999);

      const filteredUi = uiRecords.filter(record => {
        const incidentDate = new Date(record.time_of_incident);
        return !Number.isNaN(incidentDate.getTime())
          && incidentDate >= startDateObj
          && incidentDate <= endDateObj;
      });

      const visitIdsWithUi = new Set(
        filteredUi
          .filter(record => record.visit_id != null)
          .map(record => Number(record.visit_id))
      );

      const clientsToQuery = selectedClient ? [selectedClient] : clients;
      let visitsData: Visit[] = [];

      for (const client of clientsToQuery) {
        const response = await api.visits.listByProvider(
          selectedProvider.provider_id,
          1000,
          0,
          {
            startDate,
            endDate,
            clientId: Number(client.client_id),
          }
        );
        visitsData.push(...parseVisitsResponse(response));
      }

      const legacyVisits = visitsData.filter(
        visit => visit.unusual_incident_flag && !visitIdsWithUi.has(Number(visit.visit_id))
      );

      const rows: UnusualIncidentReportRow[] = [
        ...filteredUi.map(record => {
          const incidentDate = new Date(record.time_of_incident);
          return {
            date: formatDate(record.time_of_incident),
            sortDate: incidentDate.getTime(),
            clientId: Number(record.client_id),
            clientName: getClientName(Number(record.client_id)),
            hasUnusualIncident: true,
            description: record.description_of_incident || '',
            injury: record.description_of_injury || '',
            actionsTaken: record.actions_taken || '',
            causesContributingFactors: record.causes_or_contributing_factors || '',
            isMajorUnusualIncident: Boolean(record.mui),
          };
        }),
        ...legacyVisits.map(visit => {
          const dateSource = visit.visit_date || visit.scheduled_start || '';
          const incidentDate = dateSource ? new Date(dateSource) : new Date(0);
          return {
            date: formatDate(dateSource),
            sortDate: incidentDate.getTime(),
            clientId: Number(visit.client_id),
            clientName: getClientName(Number(visit.client_id)),
            hasUnusualIncident: true,
            description: visit.unusual_incident_documentation || '',
            injury: visit.unusual_incident_injury || '',
            actionsTaken: visit.unusual_incident_actions_taken || '',
            causesContributingFactors: visit.unusual_incident_cause || '',
            isMajorUnusualIncident: Boolean(visit.major_unusual_incident_flag),
          };
        }),
      ];

      rows.sort((a, b) => {
        if (a.sortDate !== b.sortDate) {
          return a.sortDate - b.sortDate;
        }
        return a.clientName.localeCompare(b.clientName);
      });

      setIncidentRows(rows);
      setPage(0);
    } catch (err) {
      console.error('Error fetching unusual incidents:', err);
      setError('Failed to load unusual incident records. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTransportations = async () => {
    if (!selectedProvider || !selectedClient || !startDate || !endDate) {
      setError('Please select a provider, client, and date range.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch visits first to get ratios and for ISP filtering
      const response = await api.visits.listByProvider(
        selectedProvider.provider_id,
        1000,
        0,
        {
          startDate,
          endDate,
          clientId: Number(selectedClient.client_id)
        }
      );
      
      let visitsData: Visit[] = [];
      if (Array.isArray(response)) {
        visitsData = response;
      } else if (response && typeof response === 'object' && 'items' in response) {
        visitsData = response.items || [];
      }
      
      // Filter by isp_id if ISP is selected
      if (selectedISP) {
        visitsData = visitsData.filter((visit) => visit.isp_id === selectedISP.isp_id);
      }
      
      setVisits(visitsData);
      
      // Fetch all transportation records
      const allTransportations = await api.transportation.list();
      const transportationsArray = Array.isArray(allTransportations) ? allTransportations : [];
      
      // Filter by provider, client, and date range
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);
      endDateObj.setHours(23, 59, 59, 999); // Include entire end date
      
      const filteredTransportations = transportationsArray.filter((trans: Transportation) => {
        if (trans.provider_id !== Number(selectedProvider.provider_id)) return false;
        if (trans.client_id !== Number(selectedClient.client_id)) return false;
        
        // Filter by isp_id if ISP is selected (via associated visit)
        if (selectedISP && trans.visit_id) {
          const associatedVisit = visitsData.find(v => v.visit_id === trans.visit_id);
          if (!associatedVisit || associatedVisit.isp_id !== selectedISP.isp_id) {
            return false;
          }
        }
        
        if (trans.transportation_date) {
          const transDate = new Date(trans.transportation_date);
          if (transDate < startDateObj || transDate > endDateObj) return false;
        } else if (trans.scheduled_start) {
          const transDate = new Date(trans.scheduled_start);
          if (transDate < startDateObj || transDate > endDateObj) return false;
        } else {
          return false;
        }
        
        return true;
      });
      
      setTransportations(filteredTransportations);
      
      // Fetch all locations
      const allLocations = await api.locations.list();
      setLocations(Array.isArray(allLocations) ? allLocations : []);

    } catch (err) {
      console.error('Error fetching transportations:', err);
      setError('Failed to load transportation records. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateReport = () => {
    setHpcPreviewReady(false);
    setOutcomePreviewReady(false);
    if (isMonthlyOfficialReport(reportType)) {
      const bounds = monthBounds(serviceYear, serviceMonth);
      setStartDate(bounds.startDate);
      setEndDate(bounds.endDate);
      void fetchVisits(bounds.startDate, bounds.endDate);
    } else if (isIspScopedOfficialReport(reportType)) {
      if (!selectedISP?.span_start_date || !selectedISP?.span_end_date) {
        setError('Please select an ISP that has both start and end span dates.');
        return;
      }
      const ispStart = selectedISP.span_start_date.split('T')[0];
      const ispEnd = selectedISP.span_end_date.split('T')[0];
      setStartDate(ispStart);
      setEndDate(ispEnd);
      void fetchVisits(ispStart, ispEnd);
    } else if (reportType === 'services') {
      fetchVisits();
    } else if (reportType === 'transportation') {
      fetchTransportations();
    } else {
      fetchUnusualIncidents();
    }
  };

  const handleDownloadUnusualIncidentOfficialPdf = async () => {
    if (!selectedProvider || !startDate || !endDate) {
      setError('Please select a provider and date range before downloading.');
      return;
    }

    try {
      setIsDownloadingOfficialPdf(true);
      setError(null);
      const { blob, filename } = await api.complianceReports.downloadUnusualIncidentLog({
        provider_id: Number(selectedProvider.provider_id),
        start_date: startDate,
        end_date: endDate,
        client_id: selectedClient ? Number(selectedClient.client_id) : undefined,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download =
        filename ||
        `Unusual_Incident_Report_Log_${selectedProvider.provider_id}_${startDate}_${endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading unusual incident PDF:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to download Unusual Incident Report Log PDF.'
      );
    } finally {
      setIsDownloadingOfficialPdf(false);
    }
  };

  const handleDownloadHpcServiceDeliveryOfficialPdf = async () => {
    if (!selectedProvider || !selectedClient) {
      setError('Please select a provider and client before downloading.');
      return;
    }

    try {
      setIsDownloadingOfficialPdf(true);
      setError(null);
      const { blob, filename } = await api.complianceReports.downloadHpcServiceDelivery({
        provider_id: Number(selectedProvider.provider_id),
        client_id: Number(selectedClient.client_id),
        year: serviceYear,
        month: serviceMonth,
        isp_id: selectedISP ? Number(selectedISP.isp_id) : undefined,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download =
        filename ||
        `HPC_Service_Delivery_${selectedClient.last_name}_${serviceYear}-${String(serviceMonth).padStart(2, '0')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading HPC service delivery PDF:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to download HPC Service Delivery Documentation PDF.'
      );
    } finally {
      setIsDownloadingOfficialPdf(false);
    }
  };

  const handleDownloadHpcOutcomeDocumentationOfficialPdf = async () => {
    if (!selectedProvider || !selectedClient || !selectedISP) {
      setError('Please select a provider, client, and ISP before downloading.');
      return;
    }

    try {
      setIsDownloadingOfficialPdf(true);
      setError(null);
      const { blob, filename } = await api.complianceReports.downloadHpcOutcomeDocumentation({
        provider_id: Number(selectedProvider.provider_id),
        client_id: Number(selectedClient.client_id),
        isp_id: Number(selectedISP.isp_id),
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download =
        filename ||
        `HPC_Outcome_Documentation_${selectedClient.last_name}_ISP${selectedISP.isp_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading HPC outcome documentation PDF:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to download HPC Outcome Documentation PDF.'
      );
    } finally {
      setIsDownloadingOfficialPdf(false);
    }
  };

  const isReportCriteriaValid = (): boolean => {
    if (!selectedProvider) {
      return false;
    }
    if (isMonthlyOfficialReport(reportType)) {
      return Boolean(selectedClient && serviceMonth && serviceYear);
    }
    if (isIspScopedOfficialReport(reportType)) {
      return Boolean(
        selectedClient &&
          selectedISP &&
          selectedISP.span_start_date &&
          selectedISP.span_end_date
      );
    }
    if (!startDate || !endDate) {
      return false;
    }
    if (reportType === 'unusual-incidents') {
      return true;
    }
    return Boolean(selectedClient);
  };

  const getCountyName = (countyId: number | undefined): string => {
    if (!countyId) return '';
    const county = ohioCounties.find(c => c.county_id === countyId);
    return county ? county.county_name : '';
  };

  const getOutcomeTitle = (outcomeId: number | undefined): string => {
    if (!outcomeId || outcomeId === 0) return '';
    // Defensive check: ensure we're looking in the outcomes array and the object has outcome_id
    const outcome = outcomes.find(o => o && 'outcome_id' in o && o.outcome_id === outcomeId);
    return outcome && 'outcome_title' in outcome ? (outcome.outcome_title || '') : '';
  };

  const getExperienceTitle = (experienceId: number | undefined): string => {
    if (!experienceId || experienceId === 0) return '';
    // Defensive check: ensure we're looking in the experiences array and the object has experience_id
    const experience = experiences.find(e => e && 'experience_id' in e && e.experience_id === experienceId);
    return experience && 'experience_title' in experience ? (experience.experience_title || '') : '';
  };

  const getSupportTitle = (supportId: number | undefined): string => {
    if (!supportId || supportId === 0) return '';
    // Defensive check: ensure we're looking in the supports array and the object has support_id
    const support = supports.find(s => s && 'support_id' in s && s.support_id === supportId);
    return support && 'support_title' in support ? (support.support_title || '') : '';
  };

  const getServiceName = (serviceId: number | string | undefined): string => {
    if (!serviceId) return '';
    const service = services.find(s => s.service_id === serviceId);
    return service ? (service.service_name || service.service_title || '') : '';
  };

  // Lookup function for 15-minute service unit conversion
  // Data-driven from service_unit_conversion table
  const getUnitsFromMinutes = (minutes: number): number => {
    // Find the conversion entry where minutes falls within the range
    const conversion = serviceUnitConversions.find(
      (conv) => minutes >= conv.minimum_minutes && minutes <= conv.maximum_minutes
    );
    
    if (conversion) {
      return conversion.units;
    }
    
    // Fallback: if no conversion found, return 0
    return 0;
  };

  const calculateUnits = (visit: Visit): string => {
    // If service_units is already set, use it
    if (visit.service_units !== undefined && visit.service_units !== null) {
      return visit.service_units.toString();
    }
    
    const service = services.find(s => s.service_id === visit.service_id);
    if (!service) {
      return '0';
    }
    
    // Calculate time difference in minutes
    let minutesDiff = 0;
    if (visit.scheduled_start && visit.scheduled_end) {
      const start = new Date(visit.scheduled_start);
      const end = new Date(visit.scheduled_end);
      minutesDiff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60));
    } else if (visit.duration) {
      // Fallback to duration if scheduled times not available
      minutesDiff = Math.floor(visit.duration / 60);
    } else {
      return '0';
    }
    
    // Apply logic from billing view SQL
    if (service.service_unit === '15-minute') {
      const units = getUnitsFromMinutes(minutesDiff);
      return units.toString();
    } else if (['Day', 'Per Day', 'Over Night', 'Part Day'].includes(service.service_unit || '')) {
      // FLOOR(TIMESTAMPDIFF(DAY, v.scheduled_start, v.scheduled_end))
      if (visit.scheduled_start && visit.scheduled_end) {
        const start = new Date(visit.scheduled_start);
        const end = new Date(visit.scheduled_end);
        const daysDiff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        return daysDiff.toString();
      }
      return '0';
    } else if (service.service_unit === 'Hour') {
      // FLOOR(TIMESTAMPDIFF(HOUR, v.scheduled_start, v.scheduled_end))
      if (visit.scheduled_start && visit.scheduled_end) {
        const start = new Date(visit.scheduled_start);
        const end = new Date(visit.scheduled_end);
        const hoursDiff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60));
        return hoursDiff.toString();
      }
      return '0';
    }
    
    return '0';
  };

  const getISPSpan = (): string => {
    if (selectedISP && selectedISP.span_start_date && selectedISP.span_end_date) {
      return `${formatDate(selectedISP.span_start_date)} - ${formatDate(selectedISP.span_end_date)}`;
    }
    if (!startDate || !endDate) return '';
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const formatAddress = (locationId: number | undefined): string => {
    if (!locationId) return '';
    const location = locations.find(l => l.location_id === locationId);
    if (!location) return '';
    
    const parts: string[] = [];
    if (location.location_address_line_1) parts.push(location.location_address_line_1);
    if (location.location_address_line_2) parts.push(location.location_address_line_2);
    if (location.location_city) parts.push(location.location_city);
    if (location.location_state) parts.push(location.location_state);
    if (location.location_zip) parts.push(location.location_zip);
    
    return parts.join(', ').toUpperCase();
  };

  const formatClientAddress = (client: Client | null): string => {
    if (!client) return '';
    const parts: string[] = [];
    if (client.address_line1) parts.push(client.address_line1);
    if (client.address_line2) parts.push(client.address_line2);
    const cityPart = [client.city, client.state].filter(Boolean).join(', ');
    const locality = [cityPart, client.zip_code].filter(Boolean).join(' ');
    if (locality) {
      parts.push(locality);
    }
    return parts.join(', ');
  };

  const getPlaceOfService = (visit: Visit): string => {
    const address = formatClientAddress(selectedClient);
    const visitType = (visit.visit_type || '').toLowerCase();

    if (visitType === 'community') {
      return address ? `Community — ${address}` : 'Community';
    }
    if (visitType === 'home') {
      return address || 'Home';
    }
    // Prefer the individual's address when visit type is missing
    return address || visit.visit_type || '';
  };

  const getVisitRatio = (visitId: number | undefined): string => {
    if (!visitId) return '';
    const visit = visits.find(v => v.visit_id === visitId);
    return visit?.ratio || '';
  };

  const calculateMiles = (transportation: Transportation): string => {
    if (transportation.ending_miles !== undefined && transportation.starting_miles !== undefined) {
      const miles = transportation.ending_miles - transportation.starting_miles;
      return miles >= 0 ? miles.toString() : '';
    }
    if (transportation.miles !== undefined) {
      return transportation.miles.toString();
    }
    return '';
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Compliance Reporting
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Selection Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          Report Criteria
        </Typography>
        <Grid container spacing={2}>
          {/* Report Type Selection */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Report Type</InputLabel>
              <Select
                value={reportType}
                label="Report Type"
                onChange={(e) => {
                  setReportType(e.target.value as ComplianceReportType);
                  setVisits([]);
                  setTransportations([]);
                  setIncidentRows([]);
                  setHpcPreviewReady(false);
                  setOutcomePreviewReady(false);
                  setPage(0);
                }}
              >
                <MenuItem value="services">Services Compliance Report</MenuItem>
                <MenuItem value="transportation">Transportation Compliance Report</MenuItem>
                <MenuItem value="unusual-incidents">Unusual Incident Report</MenuItem>
                <MenuItem value="hpc-service-delivery">HPC Service Delivery Report - PDF</MenuItem>
                <MenuItem value="hpc-outcome-documentation">HPC Outcome Documentation - PDF</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Provider Selection */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={providers}
              getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.email})`}
              value={selectedProvider}
              onChange={(_, newValue) => handleProviderChange(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Provider by Name or Email"
                  placeholder="Type to search..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              )}
              loading={isLoading}
            />
          </Grid>

          {/* Client Selection */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={clients}
              getOptionLabel={(option) => `${option.first_name} ${option.last_name}${option.medicaid_num ? ` (${option.medicaid_num})` : ''}`}
              value={selectedClient}
              onChange={(_, newValue) => handleClientChange(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={reportType === 'unusual-incidents' ? 'Select Client (Optional)' : 'Select Client'}
                  placeholder={reportType === 'unusual-incidents' ? 'All clients if not selected...' : 'Choose a client...'}
                  disabled={!selectedProvider}
                />
              )}
              loading={isLoading}
              disabled={!selectedProvider}
            />
          </Grid>

          {/* ISP Selection */}
          {reportType !== 'unusual-incidents' && (
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>
                {isIspScopedOfficialReport(reportType) ? 'ISP (Required)' : 'ISP (Optional)'}
              </InputLabel>
              <Select
                value={selectedISP?.isp_id || ''}
                label={isIspScopedOfficialReport(reportType) ? 'ISP (Required)' : 'ISP (Optional)'}
                onChange={(e) => {
                  const ispId = Number(e.target.value);
                  const isp = isps.find(i => i.isp_id === ispId);
                  handleISPChange(isp || null);
                  setHpcPreviewReady(false);
                  setOutcomePreviewReady(false);
                }}
                disabled={!selectedClient || isps.length === 0}
              >
                {!isIspScopedOfficialReport(reportType) && <MenuItem value="">None</MenuItem>}
                {isps.map((isp) => (
                  <MenuItem key={isp.isp_id} value={isp.isp_id}>
                    ISP #{isp.isp_id} {isp.description ? `- ${isp.description}` : ''}
                    {!isp.active_status ? ' (Inactive)' : ''}
                    {isp.span_start_date && isp.span_end_date 
                      ? ` (${formatDate(isp.span_start_date)} to ${formatDate(isp.span_end_date)})`
                      : isp.span_start_date 
                      ? ` (Starting ${formatDate(isp.span_start_date)})`
                      : isp.span_end_date 
                      ? ` (Until ${formatDate(isp.span_end_date)})`
                      : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          )}

          {/* Month/Year for HPC Service Delivery; ISP span is used for Outcome Documentation */}
          {isMonthlyOfficialReport(reportType) ? (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Service Month</InputLabel>
                  <Select
                    label="Service Month"
                    value={serviceMonth}
                    onChange={(e) => {
                      setServiceMonth(Number(e.target.value));
                      setHpcPreviewReady(false);
                      setOutcomePreviewReady(false);
                      setVisits([]);
                    }}
                  >
                    {MONTH_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Year</InputLabel>
                  <Select
                    label="Year"
                    value={serviceYear}
                    onChange={(e) => {
                      setServiceYear(Number(e.target.value));
                      setHpcPreviewReady(false);
                      setOutcomePreviewReady(false);
                      setVisits([]);
                    }}
                  >
                    {serviceYearOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          ) : isIspScopedOfficialReport(reportType) ? (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {selectedISP?.span_start_date && selectedISP?.span_end_date
                  ? `This report uses the selected ISP span (${formatDate(selectedISP.span_start_date)} – ${formatDate(selectedISP.span_end_date)}). A calendar row is drawn for every month in the span (with day dots for visit dates); extra pages are added when months do not fit in the form.`
                  : 'Select an ISP with start and end span dates. The report will cover that full ISP period.'}
              </Typography>
            </Grid>
          ) : (
            <>
              {/* Date Range - independent of ISP; values clamped to ISP span when ISP selected */}
              <Grid item xs={12} sm={6}>
                <AppDateField
                  label="Start Date"
                  value={startDate || null}
                  onChange={v => handleStartDateChange(v || '')}
                  minDate={
                    selectedISP?.span_start_date
                      ? ymdToLocalDate(selectedISP.span_start_date.split('T')[0])
                      : null
                  }
                  maxDate={
                    selectedISP?.span_end_date
                      ? ymdToLocalDate(selectedISP.span_end_date.split('T')[0])
                      : endDate
                        ? ymdToLocalDate(endDate)
                        : null
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AppDateField
                  label="End Date"
                  value={endDate || null}
                  onChange={v => handleEndDateChange(v || '')}
                  minDate={
                    selectedISP?.span_start_date
                      ? ymdToLocalDate(selectedISP.span_start_date.split('T')[0])
                      : startDate
                        ? ymdToLocalDate(startDate)
                        : null
                  }
                  maxDate={
                    selectedISP?.span_end_date
                      ? ymdToLocalDate(selectedISP.span_end_date.split('T')[0])
                      : null
                  }
                />
              </Grid>
            </>
          )}

          {/* Generate Report Button */}
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={handleGenerateReport}
              disabled={!isReportCriteriaValid() || isLoading}
              sx={{ mt: 1 }}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : isMonthlyOfficialReport(reportType) ? (
                'Preview Month'
              ) : isIspScopedOfficialReport(reportType) ? (
                'Preview ISP Report'
              ) : (
                'Generate Report'
              )}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Official HPC Service Delivery monthly PDF */}
      {selectedProvider && selectedClient && reportType === 'hpc-service-delivery' && hpcPreviewReady && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                HPC Service Delivery Documentation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {MONTH_OPTIONS.find((m) => m.value === serviceMonth)?.label} {serviceYear}
                {' — '}
                {visits.length} visit{visits.length === 1 ? '' : 's'}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={isDownloadingOfficialPdf ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
              onClick={() => {
                void handleDownloadHpcServiceDeliveryOfficialPdf();
              }}
              disabled={isDownloadingOfficialPdf}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Download Official Form
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Client:</strong> {selectedClient.first_name} {selectedClient.last_name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Provider:</strong> {selectedProvider.first_name} {selectedProvider.last_name}
              </Typography>
            </Grid>
          </Grid>

          {visits.length === 0 ? (
            <Typography color="text.secondary">
              No visits found for this month. You can still download the official form with header fields filled.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ border: '1px solid #ddd' }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Time In</TableCell>
                    <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Time Out</TableCell>
                    <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Ratio</TableCell>
                    <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Visit Type</TableCell>
                    <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Units</TableCell>
                    <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visits.map((visit) => (
                    <TableRow key={visit.visit_id}>
                      <TableCell sx={{ border: '1px solid #ddd' }}>
                        {formatDate(visit.visit_date || visit.scheduled_start)}
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #ddd' }}>{formatTime(visit.scheduled_start)}</TableCell>
                      <TableCell sx={{ border: '1px solid #ddd' }}>{formatTime(visit.scheduled_end)}</TableCell>
                      <TableCell sx={{ border: '1px solid #ddd' }}>{visit.ratio || ''}</TableCell>
                      <TableCell sx={{ border: '1px solid #ddd' }}>{visit.visit_type || ''}</TableCell>
                      <TableCell sx={{ border: '1px solid #ddd' }}>{visit.service_units ?? ''}</TableCell>
                      <TableCell sx={{ border: '1px solid #ddd' }}>{visit.notes || ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Official HPC Outcome Documentation ISP-scoped PDF */}
      {selectedProvider && selectedClient && reportType === 'hpc-outcome-documentation' && outcomePreviewReady && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                HPC Outcome Documentation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedISP
                  ? `ISP #${selectedISP.isp_id}${
                      selectedISP.span_start_date && selectedISP.span_end_date
                        ? ` (${formatDate(selectedISP.span_start_date)} – ${formatDate(selectedISP.span_end_date)})`
                        : ''
                    }`
                  : 'ISP'}
                {' — '}
                {visits.length} visit{visits.length === 1 ? '' : 's'}
                {outcomes.length > 0 ? ` · ${outcomes.length} outcome${outcomes.length === 1 ? '' : 's'}` : ''}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={isDownloadingOfficialPdf ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
              onClick={() => {
                void handleDownloadHpcOutcomeDocumentationOfficialPdf();
              }}
              disabled={isDownloadingOfficialPdf || !selectedISP}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Download Official Form
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Client:</strong> {selectedClient.first_name} {selectedClient.last_name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Provider:</strong> {selectedProvider.first_name} {selectedProvider.last_name}
              </Typography>
            </Grid>
          </Grid>

          {outcomes.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                ISP Outcomes (up to 3 on form)
              </Typography>
              {outcomes.slice(0, 3).map((outcome) => (
                <Typography key={outcome.outcome_id} variant="body2" sx={{ mb: 0.5 }}>
                  • {outcome.outcome_title || `Outcome #${outcome.outcome_id}`}
                  {outcome.outcome_description
                    ? ` — ${String(outcome.outcome_description).slice(0, 120)}${String(outcome.outcome_description).length > 120 ? '…' : ''}`
                    : ''}
                </Typography>
              ))}
            </Box>
          )}

          {visits.length === 0 ? (
            <Typography color="text.secondary">
              No visits found for this ISP span. You can still download the official form with outcomes and header fields filled.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ border: '1px solid #ddd' }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Outcome / Experience / Support</TableCell>
                    <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Units</TableCell>
                    <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visits.map((visit) => {
                    const outcome = outcomes.find((o) => o.outcome_id === visit.outcome_id);
                    const experience = experiences.find((e) => e.experience_id === visit.experience_id);
                    const support = supports.find((s) => s.support_id === visit.support_id);
                    const label = [outcome?.outcome_title, experience?.experience_title, support?.support_title]
                      .filter(Boolean)
                      .join(' / ') || 'Visit';
                    return (
                      <TableRow key={visit.visit_id}>
                        <TableCell sx={{ border: '1px solid #ddd' }}>
                          {formatDate(visit.visit_date || visit.scheduled_start)}
                        </TableCell>
                        <TableCell sx={{ border: '1px solid #ddd' }}>{label}</TableCell>
                        <TableCell sx={{ border: '1px solid #ddd' }}>{visit.service_units ?? ''}</TableCell>
                        <TableCell sx={{ border: '1px solid #ddd' }}>{visit.notes || ''}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Services Report Display */}
      {selectedProvider && selectedClient && reportType === 'services' && visits.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center', flex: 1 }}>
              HPC SERVICE DELIVERY AND OUTCOME REPORT
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => {
                generateServicesReportPDF({
                  provider: selectedProvider,
                  client: selectedClient,
                  visits: visits,
                  outcomes: outcomes,
                  experiences: experiences,
                  supports: supports,
                  services: services,
                  serviceUnitConversions: serviceUnitConversions,
                  locations: locations,
                  ohioCounties: ohioCounties,
                  selectedISP: selectedISP || undefined,
                  startDate: startDate,
                  endDate: endDate
                });
              }}
              sx={{ ml: 2 }}
            >
              Download PDF
            </Button>
          </Box>

          {/* Header Information */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>Individual's Name:</strong> {selectedClient.first_name} {selectedClient.last_name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>Individual's Medicaid Number:</strong> {selectedClient.medicaid_num || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>ISP Span:</strong> {selectedISP && selectedISP.span_start_date && selectedISP.span_end_date 
                  ? `${formatDate(selectedISP.span_start_date)} - ${formatDate(selectedISP.span_end_date)}`
                  : '_____________________'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>Date Range:</strong> {getISPSpan()}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>Provider's Name:</strong> {selectedProvider.first_name} {selectedProvider.last_name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>Provider's Contract Number:</strong> {selectedProvider.contract_number || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>County:</strong> {getCountyName(selectedClient.county_id)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>Provider's Electronic Signature:</strong> _____________________</Typography>
            </Grid>
          </Grid>

          {/* Visit Data Table */}
          <TableContainer>
            <Table size="small" sx={{ border: '1px solid #ddd' }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Time In</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Time Out</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Units</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Ratio</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Place of Service</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Outcome(s)</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Experience(s)</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Support(s)</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>UI?</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visits.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((visit) => (
                  <TableRow key={visit.visit_id}>
                      <TableCell sx={{ border: '1px solid #ddd' }}>{formatDate(visit.visit_date || visit.scheduled_start)}</TableCell>
                      <TableCell sx={{ border: '1px solid #ddd' }}>{formatTime(visit.scheduled_start)}</TableCell>
                      <TableCell sx={{ border: '1px solid #ddd' }}>{formatTime(visit.scheduled_end)}</TableCell>
                      <TableCell sx={{ border: '1px solid #ddd' }}>{calculateUnits(visit)}</TableCell>
                      <TableCell sx={{ border: '1px solid #ddd' }}>{visit.ratio || ''}</TableCell>
                      <TableCell sx={{ border: '1px solid #ddd' }}>{getPlaceOfService(visit)}</TableCell>
                      <TableCell sx={{ border: '1px solid #ddd' }}>{getOutcomeTitle(visit.outcome_id)}</TableCell>
                      <TableCell sx={{ border: '1px solid #ddd' }}>{getExperienceTitle(visit.experience_id)}</TableCell>
                      <TableCell sx={{ border: '1px solid #ddd' }}>{getSupportTitle(visit.support_id)}</TableCell>
                      <TableCell sx={{ border: '1px solid #ddd' }}>{visit.unusual_incident_flag ? 'Yes' : 'No'}</TableCell>
                      <TableCell sx={{ border: '1px solid #ddd' }}>{visit.notes || ''}</TableCell>
                    </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={visits.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}

      {/* Transportation Report Display */}
      {selectedProvider && selectedClient && reportType === 'transportation' && transportations.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center', flex: 1 }}>
              HPC TRANSPORTATION REPORT
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => {
                generateTransportationReportPDF({
                  provider: selectedProvider,
                  client: selectedClient,
                  transportations: transportations,
                  visits: visits,
                  locations: locations,
                  ohioCounties: ohioCounties,
                  selectedISP: selectedISP || undefined,
                  startDate: startDate,
                  endDate: endDate
                });
              }}
              sx={{ ml: 2 }}
            >
              Download PDF
            </Button>
          </Box>

          {/* Header Information */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>Individual's Name:</strong> {selectedClient.first_name} {selectedClient.last_name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>Individual's Medicaid Number:</strong> {selectedClient.medicaid_num || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>ISP Span:</strong> {selectedISP && selectedISP.span_start_date && selectedISP.span_end_date 
                  ? `${formatDate(selectedISP.span_start_date)} - ${formatDate(selectedISP.span_end_date)}`
                  : '_____________________'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>Date Range:</strong> {getISPSpan()}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>Provider's Name:</strong> {selectedProvider.first_name} {selectedProvider.last_name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>Provider's Contract Number:</strong> {selectedProvider.contract_number || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>County:</strong> {getCountyName(selectedClient.county_id)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>Provider's Electronic Signature:</strong> _____________________</Typography>
            </Grid>
          </Grid>

          {/* Transportation Data Table */}
          <TableContainer>
            <Table size="small" sx={{ border: '1px solid #ddd' }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Time In</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Time Out</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Ratio</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Starting Address</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Destination Address(es)</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Miles</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transportations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((transportation) => (
                  <TableRow key={transportation.transportation_id}>
                    <TableCell sx={{ border: '1px solid #ddd' }}>
                      {formatDate(transportation.transportation_date || transportation.scheduled_start)}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #ddd' }}>
                      {formatTime(transportation.scheduled_start)}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #ddd' }}>
                      {formatTime(transportation.scheduled_end)}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #ddd' }}>
                      {getVisitRatio(transportation.visit_id)}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #ddd' }}>
                      {formatAddress(transportation.start_location_id)}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #ddd' }}>
                      {formatAddress(transportation.end_location_id)}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #ddd' }}>
                      {calculateMiles(transportation)}
                    </TableCell>
                    <TableCell sx={{ border: '1px solid #ddd' }}>
                      {transportation.notes || ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={transportations.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}

      {selectedProvider && selectedClient && reportType === 'services' && visits.length === 0 && !isLoading && startDate && endDate && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
            No visits found for the selected criteria.
          </Typography>
        </Paper>
      )}

      {selectedProvider && selectedClient && reportType === 'transportation' && transportations.length === 0 && !isLoading && startDate && endDate && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
            No transportation records found for the selected criteria.
          </Typography>
        </Paper>
      )}

      {/* Unusual Incident Report Display */}
      {selectedProvider && reportType === 'unusual-incidents' && incidentRows.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center', flex: 1 }}>
              UNUSUAL INCIDENT REPORT
            </Typography>
            <Button
              variant="contained"
              startIcon={isDownloadingOfficialPdf ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
              onClick={handleDownloadUnusualIncidentOfficialPdf}
              disabled={isDownloadingOfficialPdf}
              sx={{ ml: 2, whiteSpace: 'nowrap' }}
            >
              Download Official Form
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Provider:</strong> {selectedProvider.first_name} {selectedProvider.last_name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Provider Contract Number:</strong> {selectedProvider.contract_number || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Client:</strong>{' '}
                {selectedClient
                  ? `${selectedClient.first_name} ${selectedClient.last_name}`
                  : 'All Clients'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Date Range:</strong> {formatDate(startDate)} - {formatDate(endDate)}
              </Typography>
            </Grid>
          </Grid>

          <TableContainer>
            <Table size="small" sx={{ border: '1px solid #ddd' }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Consumer</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Was There an Unusual Incident?</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Description (explain risk of harm)</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Injury</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Actions taken</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Causes &amp; contributing factors</TableCell>
                  <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold' }}>Was this a major unusual incident?</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {incidentRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                  <TableRow key={`${row.clientId}-${row.sortDate}-${index}`}>
                    <TableCell sx={{ border: '1px solid #ddd' }}>{row.date}</TableCell>
                    <TableCell sx={{ border: '1px solid #ddd' }}>{row.clientName}</TableCell>
                    <TableCell sx={{ border: '1px solid #ddd' }}>{row.hasUnusualIncident ? 'Yes' : 'No'}</TableCell>
                    <TableCell sx={{ border: '1px solid #ddd' }}>{row.description}</TableCell>
                    <TableCell sx={{ border: '1px solid #ddd' }}>{row.injury}</TableCell>
                    <TableCell sx={{ border: '1px solid #ddd' }}>{row.actionsTaken}</TableCell>
                    <TableCell sx={{ border: '1px solid #ddd' }}>{row.causesContributingFactors}</TableCell>
                    <TableCell sx={{ border: '1px solid #ddd' }}>{row.isMajorUnusualIncident ? 'Yes' : 'No'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={incidentRows.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Sorted by date, provider, and consumer.
          </Typography>
        </Paper>
      )}

      {selectedProvider && reportType === 'unusual-incidents' && incidentRows.length === 0 && !isLoading && startDate && endDate && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
            No unusual incident records found for the selected criteria.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ComplianceReporting;

